import { useState } from "react";
import { useRouter } from "expo-router";
import { useSetAtom } from "jotai";
import { toast } from "sonner-native";

import { useDeleteDraftApplicationsMutation } from "@/data/mutations/applicationsMutations";
import { useApplicationsCountQuery } from "@/data/queries/applicationsQueries";
import { usePendingConsentsQuery } from "@/data/queries/consentsQueries";
import { useCurrentPatientQuery } from "@/data/queries/patientsQueries";
import {
  APPLICATION_FLOW_DEFAULT_STEPS,
  APPLICATION_STEP_PATHNAMES,
  applicationResumeStepIndexByLastStep,
} from "@/domain/applications";
import { useTranslator } from "@/hooks/useTranslate";
import {
  applicationCurrentStepAtom,
  applicationTotalStepsAtom,
} from "@/stores/atoms";

/**
 * Shared "quick apply" flow actions (continue a draft / start a fresh
 * application). Extracted so the dashboard "Next steps" application card and
 * the persistent Applications section header can compose the same behavior
 * without duplicating the resume-step logic.
 */
export const useApplicationFlowActions = () => {
  const router = useRouter();
  const { t } = useTranslator();

  const { data: currentPatient } = useCurrentPatientQuery();
  const setApplicationCurrentStep = useSetAtom(applicationCurrentStepAtom);
  const setApplicationTotalSteps = useSetAtom(applicationTotalStepsAtom);
  const [isContinuing, setIsContinuing] = useState(false);
  const { refetch: refetchPendingConsents } = usePendingConsentsQuery({
    enabled: false,
  });
  const { mutateAsync: deleteDraftApplications, isPending: isDeletingDrafts } =
    useDeleteDraftApplicationsMutation();
  const { refetch: refetchMedicareDraftCount } = useApplicationsCountQuery({
    applicationType: "MEDICARE",
    status: "DRAFT",
  });
  const { refetch: refetchMarketplaceDraftCount } = useApplicationsCountQuery({
    applicationType: "MARKETPLACE",
    status: "DRAFT",
  });

  const handleContinueApplication = async () => {
    if (isContinuing) return;
    setIsContinuing(true);
    try {
      const lastStep = currentPatient?.applicationLastStep;

      // If no step recorded, check if broker consent is still pending
      if (!lastStep) {
        const [
          { data: pendingConsents },
          { data: medicareDraftCount, status: medicareStatus },
          { data: marketplaceDraftCount, status: marketplaceStatus },
        ] = await Promise.all([
          refetchPendingConsents(),
          refetchMedicareDraftCount(),
          refetchMarketplaceDraftCount(),
        ]);

        if (
          !pendingConsents ||
          medicareStatus === "error" ||
          marketplaceStatus === "error"
        ) {
          toast.error(t("common:errors.unknown"));
          return;
        }

        const hasNonMedicaidApplication =
          Boolean(medicareDraftCount) || Boolean(marketplaceDraftCount);

        if (
          pendingConsents.pendingConsents.includes("BROKER_HANDOFF") &&
          hasNonMedicaidApplication
        ) {
          setApplicationTotalSteps(APPLICATION_FLOW_DEFAULT_STEPS + 1);
          setApplicationCurrentStep(1);
          router.navigate("/(authed)/(modal)/application/broker-consent");
          return;
        }
      }

      // MMKV-persisted atom may hold a stale 13 from a prior broker-consent session.
      setApplicationTotalSteps(APPLICATION_FLOW_DEFAULT_STEPS);

      const targetStepIndex = lastStep
        ? applicationResumeStepIndexByLastStep[lastStep]
        : 0;

      setApplicationCurrentStep(
        Math.min(targetStepIndex + 1, APPLICATION_FLOW_DEFAULT_STEPS),
      );

      const trail = APPLICATION_STEP_PATHNAMES.slice(0, targetStepIndex + 1);
      for (const pathname of trail) {
        router.push(pathname);
      }
    } finally {
      setIsContinuing(false);
    }
  };

  const handleNewApplication = async () => {
    try {
      await deleteDraftApplications();
      router.navigate("/(authed)/(modal)/application/start");
    } catch {
      toast.error(t("common:errors.unknown"));
    }
  };

  return {
    handleContinueApplication,
    isContinuing,
    handleNewApplication,
    isDeletingDrafts,
  };
};
